import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { Header } from '../../../../../common/ui/styled';
import { Add } from './add';
import { Holiday } from './holiday';
import { ImportCalendar } from './import';
import messages from './messages';
import { useHolidaysState } from './state';
import type { Props } from './types';

const HolidaysView = ({ intl: { formatMessage }, list, getFormValues, setHolidays }: Props) => {
	const [{ holidays }, { onAdd, onRemove, onSave, onImport }] = useHolidaysState(
		list,
		getFormValues,
	);

	useEffect(() => {
		setHolidays(holidays);
	}, [holidays]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<Header>
				{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
				<h2> {formatMessage(messages.holidays)} </h2>
				<AddButtonContainer>
					<ImportCalendar onImport={onImport} />
					<Add onAdd={onAdd} />
				</AddButtonContainer>
			</Header>
			<p>
				{formatMessage(messages.holidaysDescription)}
				{formatMessage(messages.importHolidaysDescription)}
			</p>
			<br />
			{Boolean(holidays.length) && (
				<>
					<HeaderRow>
						<HeaderLabel>{formatMessage(messages.name)}</HeaderLabel>
						<HeaderLabel>{formatMessage(messages.date)}</HeaderLabel>
						<HeaderLabel>{formatMessage(messages.frequency)}</HeaderLabel>
					</HeaderRow>

					{/* Added isRecurring to default as false to make it compatible with type, this
                        will not cause any side effect. */}
					{holidays.map(({ name, iso8601Date, isRecurring = false, fieldId, isNew }) => (
						<Holiday
							key={fieldId}
							fieldId={fieldId}
							defaultName={name}
							defaultDate={iso8601Date}
							isRecurring={isRecurring}
							isNew={Boolean(isNew)}
							onRemove={onRemove}
							onSave={onSave}
						/>
					))}
				</>
			)}
		</>
	);
};

export const Holidays = injectIntl(HolidaysView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddButtonContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderRow = styled.div({
	display: 'grid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	gridGap: `${gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	gridTemplateColumns: `1fr 1fr 1fr ${gridSize * 4}px ${gridSize * 4}px`,
	justifyItems: 'start',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderLabel = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.DN0),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	width: '100%',
	fontWeight: 'bold',
});
